import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, KeyRound, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error(T('Une erreur est survenue, réessayez.', 'Something went wrong, try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4 bg-[#FBF8F3]" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#0F4C5C]/10 shadow-sm p-8">
        {sent ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-[#0F4C5C] mb-2">{T('Email envoyé !', 'Email sent!')}</h1>
            <p className="text-sm text-muted-foreground mb-6">
              {T('Si un compte existe avec cette adresse, vous recevrez un lien de réinitialisation valable 1 heure. Pensez à vérifier vos spams.',
                 'If an account exists with this address, you will receive a reset link valid for 1 hour. Check your spam folder.')}
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {T('Retour à la connexion', 'Back to login')}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#0F4C5C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-7 h-7 text-[#0F4C5C]" />
              </div>
              <h1 className="text-2xl font-bold text-[#0F4C5C] mb-1">{T('Mot de passe oublié ?', 'Forgot password?')}</h1>
              <p className="text-sm text-muted-foreground">
                {T('Saisissez votre email, nous vous enverrons un lien de réinitialisation.',
                   'Enter your email and we will send you a reset link.')}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    className="pl-10 h-12" required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary h-12" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : T('Envoyer le lien', 'Send reset link')}
              </Button>
              <Link to="/login" className="block text-center text-sm text-[#0F4C5C] hover:underline">
                {T('Retour à la connexion', 'Back to login')}
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(T('Le mot de passe doit contenir au moins 8 caractères', 'Password must be at least 8 characters'));
      return;
    }
    if (password !== confirm) {
      toast.error(T('Les mots de passe ne correspondent pas', 'Passwords do not match'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success(T('Mot de passe réinitialisé ! Connectez-vous.', 'Password reset! Please log in.'));
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || T('Lien invalide ou expiré', 'Invalid or expired link'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4 bg-[#FBF8F3]" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#0F4C5C]/10 shadow-sm p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#0F4C5C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-[#0F4C5C]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F4C5C] mb-1">{T('Nouveau mot de passe', 'New password')}</h1>
          <p className="text-sm text-muted-foreground">
            {T('Choisissez un nouveau mot de passe (8 caractères minimum).', 'Choose a new password (min. 8 characters).')}
          </p>
        </div>
        {!token ? (
          <div className="text-center" data-testid="reset-no-token">
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              {T('Lien invalide. Refaites une demande de réinitialisation.', 'Invalid link. Please request a new reset.')}
            </p>
            <Link to="/forgot-password">
              <Button variant="outline" className="w-full">{T('Demander un nouveau lien', 'Request a new link')}</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="new-password">{T('Nouveau mot de passe', 'New password')}</Label>
              <Input
                id="new-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="h-12" required minLength={8}
                data-testid="reset-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{T('Confirmer le mot de passe', 'Confirm password')}</Label>
              <Input
                id="confirm-password" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••" className="h-12" required minLength={8}
                data-testid="reset-confirm-input"
              />
            </div>
            <Button type="submit" className="w-full btn-primary h-12" disabled={loading} data-testid="reset-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : T('Réinitialiser le mot de passe', 'Reset password')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
